import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import { formatCurrency } from '../utils/currency';
import SearchableSelect from '../components/SearchableSelect';
import './FinancialTransactions.css';

function FinanceReports() {
  const [activeReport, setActiveReport] = useState('cashbook');
  const [transactions, setTransactions] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    startDate: new Date(new Date().setDate(1)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    vendorId: '',
    projectId: '',
    customerId: '',
    employeeId: '',
    payeeName: ''
  });

  useEffect(() => {
    fetchVendors();
    fetchProjects();
    fetchCustomers();
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (activeReport) {
      fetchReportData();
    }
  }, [activeReport, filters]);

  const fetchVendors = async () => {
    try {
      const response = await axios.get('/vendors?size=1000');
      const data = response.data.data?.content || response.data.content || response.data.data || [];
      setVendors(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get('/projects?size=1000');
      const data = response.data.data?.content || response.data.content || response.data.data || [];
      setProjects(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get('/customers?size=1000');
      const data = response.data.data?.content || response.data.content || response.data.data || [];
      setCustomers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get('/employees?size=1000');
      const data = response.data?.employees || response.data?.data?.employees || response.data?.data || [];
      setEmployees(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchReportData = async () => {
    setLoading(true);
    try {
      let url = '/financial-transactions';

      if (activeReport === 'cashbook') {
        url += `/date-range?startDate=${filters.startDate}&endDate=${filters.endDate}`;
      } else if (activeReport === 'vendor-ledger') {
        if (filters.vendorId) {
          url += `/vendor/${filters.vendorId}`;
        } else {
          url += `/date-range?startDate=${filters.startDate}&endDate=${filters.endDate}`;
        }
      } else if (activeReport === 'project-ledger') {
        if (filters.projectId) {
          url += `/project/${filters.projectId}`;
        } else {
          url += `/date-range?startDate=${filters.startDate}&endDate=${filters.endDate}`;
        }
      } else if (activeReport === 'customer-payments') {
        if (filters.customerId) {
          url += `/customer/${filters.customerId}`;
        } else {
          url += `/date-range?startDate=${filters.startDate}&endDate=${filters.endDate}`;
        }
      } else if (activeReport === 'employee-ledger') {
        if (filters.employeeId) {
          url += `/employee/${filters.employeeId}`;
        } else {
          url += `/date-range?startDate=${filters.startDate}&endDate=${filters.endDate}`;
        }
      } else if (activeReport === 'other-payment-ledger') {
        url += `/other-payments`;
      } else if (activeReport === 'vendor-outstanding') {
        if (filters.vendorId) {
          url += `/vendor/${filters.vendorId}`;
        } else {
          url += `/date-range?startDate=${filters.startDate}&endDate=${filters.endDate}`;
        }
      } else {
        url += `/date-range?startDate=${filters.startDate}&endDate=${filters.endDate}`;
      }

      const response = await axios.get(url);
      const data = response.data.data || response.data || [];
      let list = Array.isArray(data) ? data : [];

      if (activeReport !== 'cashbook') {
        const start = new Date(filters.startDate);
        const end = new Date(filters.endDate);
        end.setHours(23, 59, 59, 999);

        list = list.filter(txn => {
          const txnDate = new Date(txn.transactionDate);
          const inDateRange = txnDate >= start && txnDate <= end;
          if (!inDateRange) return false;

          if (activeReport === 'other-payment-ledger' && filters.payeeName) {
            return txn.payeeName?.toLowerCase().includes(filters.payeeName.toLowerCase());
          }

          if (activeReport === 'employee-ledger' && !filters.employeeId) {
            return txn.employeeId != null || txn.employeeName != null;
          }

          if (activeReport === 'vendor-ledger' && !filters.vendorId) {
            return txn.vendorId != null || txn.vendorName != null;
          }

          if (activeReport === 'project-ledger' && !filters.projectId) {
            return txn.projectId != null || txn.projectName != null;
          }

          if (activeReport === 'customer-payments' && !filters.customerId) {
            return txn.customerId != null || txn.customerName != null;
          }

          return true;
        });
      }

      setTransactions(list);
    } catch (error) {
      console.error('Error fetching report data:', error);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const calculateTotals = () => {
    const income = transactions
      .filter(t => t.transactionType === 'INCOME')
      .reduce((sum, t) => sum + (t.amount || 0), 0);
    
    const expense = transactions
      .filter(t => t.transactionType === 'EXPENSE')
      .reduce((sum, t) => sum + (t.amount || 0), 0);
    
    const purchase = transactions
      .filter(t => t.transactionType === 'PURCHASE')
      .reduce((sum, t) => sum + (t.amount || 0), 0);
    
    const payment = transactions
      .filter(t => t.transactionType === 'PAYMENT')
      .reduce((sum, t) => sum + (t.amount || 0), 0);

    return { income, expense, purchase, payment, balance: income - expense - purchase + payment };
  };

  const totals = calculateTotals();

  const reportTypes = [
    { key: 'cashbook', label: 'Cashbook', icon: '📖' },
    { key: 'vendor-ledger', label: 'Vendor Ledger', icon: '🏪' },
    { key: 'project-ledger', label: 'Project Ledger', icon: '🏗' },
    { key: 'customer-payments', label: 'Customer Payments', icon: '💵' },
    { key: 'employee-ledger', label: 'Employee Ledger', icon: '👤' },
    { key: 'other-payment-ledger', label: 'Other Payment Ledger', icon: '✏️' },
    { key: 'vendor-outstanding', label: 'Vendor Outstanding', icon: '💸' }
  ];

  return (
    <div className="financial-transactions">
      <h1>Financial Reports</h1>
      <p>View cashbook, ledgers, and outstanding reports</p>

      <div className="report-selector">
        {reportTypes.map(report => (
          <button
            key={report.key}
            className={`report-btn ${activeReport === report.key ? 'active' : ''}`}
            onClick={() => setActiveReport(report.key)}
          >
            <span className="report-icon">{report.icon}</span>
            <span>{report.label}</span>
          </button>
        ))}
      </div>

      <div className="rpt-filters">
        <div className="rpt-filter-row">
          <label className="rpt-filter-label">Start Date</label>
          <input type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} />
        </div>

        <div className="rpt-filter-row">
          <label className="rpt-filter-label">End Date</label>
          <input type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} />
        </div>

        {(activeReport === 'vendor-ledger' || activeReport === 'vendor-outstanding') && (
          <div className="rpt-filter-row">
            <label className="rpt-filter-label">Vendor</label>
            <SearchableSelect
              options={vendors}
              value={filters.vendorId}
              onChange={(val) => setFilters(prev => ({ ...prev, vendorId: val }))}
              placeholder="Search & select vendor..."
              searchPlaceholder="Search vendor by name, phone..."
              allOptionLabel="Select Vendor (All)"
              getOptionLabel={(v) => v.name}
              getOptionValue={(v) => v.id}
              getOptionSubtext={(v) => v.phone ? `📞 ${v.phone}` : v.vendorType || ''}
            />
          </div>
        )}

        {activeReport === 'project-ledger' && (
          <div className="rpt-filter-row">
            <label className="rpt-filter-label">Project</label>
            <SearchableSelect
              options={projects}
              value={filters.projectId}
              onChange={(val) => setFilters(prev => ({ ...prev, projectId: val }))}
              placeholder="Search & select project..."
              searchPlaceholder="Search project by name, client..."
              allOptionLabel="Select Project (All)"
              getOptionLabel={(p) => p.name}
              getOptionValue={(p) => p.id}
              getOptionSubtext={(p) => {
                const parts = [];
                if (p.customerName || p.customer?.name) parts.push(`Client: ${p.customerName || p.customer?.name}`);
                if (p.status) parts.push(p.status);
                return parts.join(' • ');
              }}
            />
          </div>
        )}

        {activeReport === 'customer-payments' && (
          <div className="rpt-filter-row">
            <label className="rpt-filter-label">Customer</label>
            <SearchableSelect
              options={customers}
              value={filters.customerId}
              onChange={(val) => setFilters(prev => ({ ...prev, customerId: val }))}
              placeholder="Search & select customer..."
              searchPlaceholder="Search customer by name, phone..."
              allOptionLabel="Select Customer (All)"
              getOptionLabel={(c) => c.name}
              getOptionValue={(c) => c.id}
              getOptionSubtext={(c) => c.phone ? `📞 ${c.phone}` : c.email ? `✉ ${c.email}` : ''}
            />
          </div>
        )}

        {activeReport === 'employee-ledger' && (
          <div className="rpt-filter-row">
            <label className="rpt-filter-label">Employee</label>
            <SearchableSelect
              options={employees}
              value={filters.employeeId}
              onChange={(val) => setFilters(prev => ({ ...prev, employeeId: val }))}
              placeholder="Search & select employee..."
              searchPlaceholder="Search employee by name, code..."
              allOptionLabel="Select Employee (All)"
              getOptionLabel={(emp) => emp.name || (emp.user && emp.user.name) || emp.userName || `Employee #${emp.id}`}
              getOptionValue={(emp) => emp.id}
              getOptionSubtext={(emp) => emp.employeeCode ? `Code: ${emp.employeeCode}` : ''}
            />
          </div>
        )}

        {activeReport === 'other-payment-ledger' && (
          <div className="rpt-filter-row">
            <label className="rpt-filter-label">Payee Name</label>
            <input
              type="text"
              name="payeeName"
              placeholder="Search payee..."
              value={filters.payeeName}
              onChange={handleFilterChange}
            />
          </div>
        )}
      </div>

      <div className="report-summary">
        <div className="summary-card income">
          <span>Total Income</span>
          <strong>{formatCurrency(totals.income)}</strong>
        </div>
        <div className="summary-card expense">
          <span>Total Expense</span>
          <strong>{formatCurrency(totals.expense)}</strong>
        </div>
        <div className="summary-card purchase">
          <span>Total Purchase</span>
          <strong>{formatCurrency(totals.purchase)}</strong>
        </div>
        <div className="summary-card payment">
          <span>Total Payment</span>
          <strong>{formatCurrency(totals.payment)}</strong>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading report data...</div>
      ) : (
        <div className="report-table-container">
          <table className="report-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Voucher</th>
                <th>Type</th>
                <th>Particulars</th>
                <th>Account Head</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {transactions.length === 0 ? (
                <tr>
                  <td colSpan="6" className="no-data">
                    No transactions found
                  </td>
                </tr>
              ) : (
                transactions.map(txn => (
                  <tr key={txn.id}>
                    <td>{new Date(txn.transactionDate).toLocaleDateString()}</td>
                    <td>{txn.voucherNo}</td>
                    <td>
                      <span className={`type-badge ${txn.transactionType.toLowerCase()}`}>
                        {txn.transactionType}
                      </span>
                    </td>
                    <td>
                      {txn.customerName || txn.vendorName || txn.projectName || '-'}
                    </td>
                    <td>{txn.accountHeadName || '-'}</td>
                    <td className="amount">{formatCurrency(txn.amount)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default FinanceReports;
